import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { UserMultiSelect } from "./MeetingUserMultiSelect";
import { useAuthStore } from "@/store/auth";
import { toDatetimeLocalValue } from "@/utils/datetime";
import { usePortfolioOptions } from "@/features/power_projects/hooks";
import { useCreateMeeting, useUpdateMeeting } from "../hooks/useMeetings";
import type { MeetingDetail, MeetingKind, RecurrenceRule } from "../types";
import { RecurrenceEditor } from "./RecurrenceEditor";

const schema = z.object({
  title: z.string().min(1, "Podaj tytuł spotkania.").max(200),
  description: z.string().max(5000).optional().default(""),
  date: z.string().min(1, "Podaj datę."),
  time: z.string().min(1, "Podaj godzinę."),
  duration_minutes: z.coerce.number().int().min(5).max(12 * 60),
  send_sms_reminder: z.boolean().default(false),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Edit mode when provided. */
  meeting?: MeetingDetail | null;
  /** Preseed for the ZE-project tab. */
  zeProjectId?: string | null;
  onCreated?: (meeting: MeetingDetail) => void;
}

const INPUT =
  "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-primary)]";
const LABEL = "mb-1 block text-sm text-[var(--color-text-secondary)]";

const DEFAULT_RECURRENCE: RecurrenceRule = {
  freq: "weekly",
  weekday: 0,
  monthday: null,
  time: "09:00",
  until: null,
};

export function MeetingFormModal({
  open,
  onClose,
  meeting = null,
  zeProjectId = null,
  onCreated,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const hasDlc01 = !!user?.accessible_dlc_modules?.includes("dlc_01");

  const editing = !!meeting;
  const [kind, setKind] = useState<Exclude<MeetingKind, "instant">>(
    meeting?.kind === "recurring" ? "recurring" : "planned",
  );
  const [recurrence, setRecurrence] = useState<RecurrenceRule>(
    meeting?.recurrence ?? DEFAULT_RECURRENCE,
  );
  const [participantIds, setParticipantIds] = useState<string[]>(
    meeting
      ? meeting.participants
          .filter((p) => p.role !== "organizer")
          .map((p) => p.user.id)
      : [],
  );
  const [zeProject, setZeProject] = useState<string>(
    meeting?.ze_project ?? zeProjectId ?? "",
  );
  const [serverError, setServerError] = useState<string | null>(null);

  const startDate = meeting ? new Date(meeting.starts_at) : null;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: meeting?.title ?? "",
      description: meeting?.description ?? "",
      date: toDatetimeLocalValue(startDate).slice(0, 10),
      time: startDate
        ? `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`
        : "10:00",
      duration_minutes: meeting?.duration_minutes ?? 60,
      send_sms_reminder: meeting?.send_sms_reminder ?? false,
    },
  });

  const createMutation = useCreateMeeting();
  const updateMutation = useUpdateMeeting(meeting?.id ?? "");

  const portfolio = usePortfolioOptions(hasDlc01 && open);
  const projectOptions = useMemo(
    () =>
      (portfolio.data ?? []).map((row: { id: string; project_name?: string; project_short_name?: string }) => ({
        value: row.id,
        label: row.project_short_name || row.project_name || row.id,
      })),
    [portfolio.data],
  );

  const submit = handleSubmit(async (values) => {
    setServerError(null);
    const startsAt = new Date(`${values.date}T${values.time}`);
    const payload = {
      title: values.title,
      description: values.description ?? "",
      kind,
      starts_at: startsAt.toISOString(),
      duration_minutes: values.duration_minutes,
      send_sms_reminder: values.send_sms_reminder,
      participant_ids: participantIds,
      ze_project: hasDlc01 ? zeProject || null : undefined,
      recurrence: kind === "recurring" ? recurrence : null,
    };
    try {
      if (editing && meeting) {
        await updateMutation.mutateAsync(payload);
      } else {
        const created = await createMutation.mutateAsync(payload);
        onCreated?.(created);
      }
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { errors?: unknown } } };
      setServerError(
        typeof e.response?.data?.errors === "string"
          ? e.response.data.errors
          : "Nie udało się zapisać spotkania. Sprawdź pola formularza.",
      );
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Edytuj spotkanie" : "Nowe spotkanie"}
      size="lg"
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={LABEL}>Tytuł</label>
          <input type="text" {...register("title")} className={INPUT} autoFocus />
          {errors.title && (
            <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
          )}
        </div>

        <div>
          <label className={LABEL}>Opis (opcjonalnie)</label>
          <textarea rows={2} {...register("description")} className={INPUT} />
        </div>

        {!editing && (
          <div className="flex gap-2">
            {(
              [
                ["planned", "Zaplanowane"],
                ["recurring", "Cykliczne"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setKind(value)}
                className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                  kind === value
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                    : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div>
            <label className={LABEL}>
              {kind === "recurring" ? "Pierwszy dzień" : "Data"}
            </label>
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <DatePicker value={field.value} onChange={field.onChange} />
              )}
            />
            {errors.date && (
              <p className="mt-1 text-xs text-red-500">{errors.date.message}</p>
            )}
          </div>
          <div>
            <label className={LABEL}>Godzina</label>
            <input type="time" {...register("time")} className={INPUT} />
          </div>
          <div>
            <label className={LABEL}>Czas trwania (min)</label>
            <input
              type="number"
              min={5}
              step={5}
              {...register("duration_minutes")}
              className={INPUT}
            />
          </div>
        </div>

        {kind === "recurring" && (
          <RecurrenceEditor value={recurrence} onChange={setRecurrence} />
        )}

        <UserMultiSelect selected={participantIds} onChange={setParticipantIds} />

        {hasDlc01 && (
          <div>
            <label className={LABEL}>Projekt ZE (opcjonalnie)</label>
            <SearchableSelect
              options={projectOptions}
              value={zeProject}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setZeProject(e.target.value)
              }
              placeholder="Wybierz projekt ZE…"
            />
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <input type="checkbox" {...register("send_sms_reminder")} />
          Przypomnienie SMS 15 minut przed spotkaniem
        </label>

        {serverError && <p className="text-sm text-red-500">{serverError}</p>}

        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {editing ? "Zapisz zmiany" : "Utwórz spotkanie"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
